import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import Main from '../Layout/Main';
import WSNotificationHandler from '../WSNotificationHandler';
import CheckAuthentication from '../CheckAuthentication';
import CheckAuthorization from '../CheckAuthorization';
import CheckOrganization from '../CheckOrganization';
import withOrganization from '../../hooks/withOrganization';
import CaseFeedback from '../../pages/CaseFeedback';
import UserActivityHandler from '../UserActivityHandler';
import GlobalBackdrop from '../GlobalBackdrop';
import { fetchCurrentUserAction } from '../../reducers/auth';
import { EchoProvider } from '../EchoProvider';
import DibsOpener from '../DibsOpener';

const DetectOrganization = ({ detect, children, ...props }) => {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(state => state.auth.currentOrganization);
  const organizationId = props.match.params.organizationId
    ? Number(props.match.params.organizationId)
    : null;

  useEffect(() => {
    if (!detect || !organizationId) {
      return;
    }
    if (!currentOrganization || currentOrganization.id !== organizationId) {
      dispatch(fetchCurrentUserAction(organizationId));
    }
  }, [organizationId, currentOrganization, detect, dispatch]);

  if (detect) {
    return withOrganization({ organizationId, children });
  }
  return children({ organization: null });
};

DetectOrganization.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  match: PropTypes.object.isRequired,
  children: PropTypes.func.isRequired
};

const PrivateRoute = ({
  component: Component,
  layout: ContainerLayout,
  roles,
  profiles,
  initialSetupRequired,
  organizationRoles,
  checkOrganization,
  unauthorizedRedirectUrl,
  detectOrganization,
  ...rest
}) => {
  const user = useSelector(state => state.auth.user);
  return (
    <Route
      {...rest}
      render={props => {
        return (
          <CheckAuthentication
            path={`${rest.location.pathname}${rest.location.search}`}
            roles={roles}
          >
            <CheckAuthorization
              profiles={profiles}
              roles={roles}
              unauthorizedRedirectUrl={unauthorizedRedirectUrl}
            >
              <EchoProvider>
                <ContainerLayout>
                  <GlobalBackdrop />
                  <WSNotificationHandler user={user} />
                  <DibsOpener />
                  <UserActivityHandler user={user} />
                  <DetectOrganization detect={detectOrganization} {...props}>
                    {({ organization }) => {
                      let componentProps = { ...props };
                      if (organization && organization.data) {
                        componentProps = { organization, ...props };
                      }
                      return (
                        <CheckOrganization
                          organization={organization && organization.data}
                          organizationRoles={organizationRoles}
                          initialSetupRequired={initialSetupRequired}
                          checkOrganization={checkOrganization}
                        >
                          <CaseFeedback />
                          <Component {...componentProps} />
                        </CheckOrganization>
                      );
                    }}
                  </DetectOrganization>
                </ContainerLayout>
              </EchoProvider>
            </CheckAuthorization>
          </CheckAuthentication>
        );
      }}
    />
  );
};

PrivateRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
  layout: PropTypes.func,
  roles: PropTypes.arrayOf(PropTypes.string),
  initialSetupRequired: PropTypes.bool,
  organizationRoles: PropTypes.arrayOf(PropTypes.string),
  profiles: PropTypes.arrayOf(PropTypes.string),
  checkOrganization: PropTypes.bool,
  detectOrganization: PropTypes.bool,
  unauthorizedRedirectUrl: PropTypes.string
};

PrivateRoute.defaultProps = {
  layout: Main,
  roles: [],
  initialSetupRequired: true,
  organizationRoles: [],
  checkOrganization: true,
  profiles: ['normal', 'management'],
  detectOrganization: false,
  unauthorizedRedirectUrl: null
};

export default PrivateRoute;
